/**
 * Team-side helpers for published workspace templates.
 *
 * Built-in templates already look like an assessment (`question_sets`). A
 * workspace subscription stores the editor document (`pages` / `elements`),
 * which the live create API cannot persist as questions. These helpers decide
 * whether to skip the configure step and reshape that document into the
 * payload `Assessments.create` expects.
 */

const LIVE_TYPES = new Set(['live', 'live-assessment']);

const QUESTION_SET_PAGE_TYPES = new Set(['question-set', 'questionSet']);

export function isLiveTemplate(template) {
  return LIVE_TYPES.has(template?.type);
}

const CONFIGURE_TYPES = new Set(['survey', ...LIVE_TYPES]);

/**
 * Survey and live templates always get the configure step, which doubles as
 * the preview of their questions. Anything else has no configure UI and is
 * created straight away.
 */
export function shouldSkipConfigure(template) {
  if (!template) {
    return false;
  }
  return !CONFIGURE_TYPES.has(template.type);
}

function pagesList(pages) {
  if (Array.isArray(pages)) {
    return pages;
  }
  if (pages && typeof pages === 'object') {
    return Object.values(pages);
  }
  return [];
}

function isQuestionSetPage(page) {
  return QUESTION_SET_PAGE_TYPES.has(page?.pageType);
}

function answersFromElement(question, answerSet) {
  const answers = (question.answers ?? []).map((answer, index) => {
    const next = {
      text: answer.text,
      value: answer.value,
      ordinal: answer.ordinal ?? index,
    };
    if (answer.is_na) {
      next.is_na = true;
    }
    return next;
  });

  const includeNA = answerSet?.includeNA ?? question.includeNA;
  const naText = answerSet?.naText ?? question.naText;
  if (includeNA && !answers.some((answer) => answer.is_na)) {
    answers.push({
      value: null,
      text: naText || 'N/A',
      is_na: true,
      ordinal: answers.length,
    });
  }

  return answers;
}

function questionFromElement(question, answerSet) {
  const decoration = question.config?.decoration ?? question.decoration;
  const config =
    decoration == null ? question.config : { ...question.config, decoration };

  return {
    text: question.text,
    key: question.key,
    type: question.type,
    ordinal: question.ordinal,
    config,
    answers: answersFromElement(question, answerSet),
  };
}

export function questionSetsFromPages(pages, answerSets = {}) {
  return pagesList(pages)
    .filter(isQuestionSetPage)
    .map((page) => ({
      name: page.name,
      key: page.key,
      ordinal: page.ordinal,
      // Survey topics; the keys index the assessment's config.categories
      ...(page.category ? { category: page.category } : {}),
      questions: (page.elements ?? [])
        .filter((element) => element.elementType === 'question')
        .map((element) =>
          questionFromElement(element, answerSets[element.answerSetId])
        ),
    }));
}

/**
 * Shape a template (built-in or workspace subscription) into the assessment
 * body posted to create. Built-in JSON is returned as-is; a pages document
 * is flattened into `question_sets`.
 */
export function assessmentPayloadFromTemplate(template) {
  const { assessment = {}, type } = template ?? {};

  if (Array.isArray(assessment.question_sets)) {
    return { type, ...assessment };
  }

  const { pages, answerSets, ...rest } = assessment;

  return {
    ...rest,
    type,
    name: assessment.name ?? template?.name,
    key: assessment.key ?? template?.key,
    question_sets: questionSetsFromPages(pages, answerSets),
  };
}
